import { FlutterBridge } from './flutterBridge'

/**
 * Bridge 版本与能力管理
 *
 * 用于在调用 Flutter 方法前，先确认当前 App 是否支持该能力。
 * 避免旧版 App + 新版 H5 时，调用不存在的方法导致 3 秒超时才报错。
 *
 * 使用示例：
 * ```typescript
 * import { bridgeCapabilities } from '@/utils/bridgeCapabilities'
 *
 * // 检查方法是否支持
 * if (await bridgeCapabilities.supports('shareToWechat')) {
 *   await FlutterBridge.call('shareToWechat', { ... })
 * } else {
 *   // 提示用户升级 App，或走降级逻辑
 * }
 *
 * // 检查最低版本
 * if (await bridgeCapabilities.requiresVersion(3)) {
 *   // 使用 v3 新增功能
 * }
 * ```
 */

interface BridgeInfo {
  /** Bridge 协议版本号，每次新增/修改接口时 +1 */
  bridgeVersion: number
  /** 当前 App 支持的 handler 名称列表 */
  capabilities: string[]
}

/**
 * 旧版 App（不支持 getBridgeInfo）的默认能力集，视为 v1
 * 对应 jz_js_util.dart 最初注册的那批 handler：
 * setupCommunication 注册了 flutterMethod / getAppLogin / goLogin / goRecharge / saveImage
 * flutterMethod 内部支持的 action：showToast / matchDetail / statusBarHeight / appBack / getChannelId / saveImage / pageLoaded
 */
const LEGACY_BRIDGE_INFO: BridgeInfo = {
  bridgeVersion: 1,
  capabilities: ['flutterMethod', 'getAppLogin', 'goLogin', 'goRecharge', 'saveImage'],
}

class BridgeCapabilities {
  private _info: BridgeInfo | null = null
  private _fetching: Promise<BridgeInfo> | null = null

  /**
   * 获取 Bridge 信息（带缓存，只请求一次）
   */
  async getInfo(): Promise<BridgeInfo> {
    if (this._info) return this._info

    // 防止并发重复请求
    if (this._fetching) return this._fetching

    this._fetching = FlutterBridge.call('getBridgeInfo')
      .then((result) => {
        this._info = result as BridgeInfo
        console.log(
          `[BridgeCapabilities] App Bridge v${this._info.bridgeVersion}，支持：`,
          this._info.capabilities,
        )
        return this._info
      })
      .catch(() => {
        // 旧版 App 没有 getBridgeInfo，降级为 v1
        this._info = LEGACY_BRIDGE_INFO
        console.warn('[BridgeCapabilities] 旧版 App，不支持 getBridgeInfo，降级为 Bridge v1')
        return this._info
      })
      .finally(() => {
        this._fetching = null
      })

    return this._fetching
  }

  /**
   * 检查 App 是否支持指定的 Bridge 方法
   * @param method handler 名称，如 'saveImage'
   */
  async supports(method: string): Promise<boolean> {
    const info = await this.getInfo()
    return info.capabilities.includes(method)
  }

  /**
   * 检查 App Bridge 版本是否满足最低要求
   * @param minVersion 最低版本号
   */
  async requiresVersion(minVersion: number): Promise<boolean> {
    const info = await this.getInfo()
    return info.bridgeVersion >= minVersion
  }

  /**
   * 获取当前 Bridge 版本号
   */
  async getVersion(): Promise<number> {
    const info = await this.getInfo()
    return info.bridgeVersion
  }

  /** 重置缓存（一般仅测试时使用） */
  reset() {
    this._info = null
    this._fetching = null
  }
}

export const bridgeCapabilities = new BridgeCapabilities()
